import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { Confirmation, ToasterService } from '@abp/ng.theme.shared';
import { LocalizationPipe } from '@abp/ng.core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, finalize, of, take } from 'rxjs';
import { IModalContent } from 'projects/shared/src/lib/zn-modal-wrapper/interfaces/IModalContent';
import {
  createFormCanSaveSignal,
  DEFAULT_TEXTAREA_MAX_LENGTH,
  ZnConfirmation,
  ZnSingleSelect,
} from '@zendra/shared';
import {
  type EditActivityDistributionInputDto,
  type EditInventoryDistributionInputDto,
  StandTransformationHistoryPlanningOrchestratorService,
} from 'orchestrator';
import { StandTransformationHistoryDto } from '../../proxy/geographic/dtos/stand-transformation-history';

type StandTransformationHistoryViewRow = StandTransformationHistoryDto & {
  [key: string]: unknown;
  flowDate?: string | null;
  flowType?: { name?: string | null } | null;
  flowUser?: { name?: string | null } | null;
  inventoryDistributionStatus?: { name?: string | null } | null;
  inventoryDistributionUser?: { name?: string | null } | null;
  activityDistributionStatus?: { name?: string | null } | null;
  activityDistributionUser?: { name?: string | null } | null;
};

export type StandTransformationHistoryDetailMode =
  | 'view'
  | 'editInventoryDistribution'
  | 'editActivityDistribution';

@Component({
  selector: 'app-stand-transformation-history-detail-modal',
  imports: [CommonModule, LocalizationPipe, ReactiveFormsModule, ZnSingleSelect],
  templateUrl: './stand-transformation-history-detail-modal.html',
  styleUrl: './stand-transformation-history-detail-modal.scss',
})
export class StandTransformationHistoryDetailModalComponent implements IModalContent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly formBuilder = inject(FormBuilder);
  private readonly orchestratorService = inject(
    StandTransformationHistoryPlanningOrchestratorService,
  );
  private readonly confirmationService = inject(ZnConfirmation);
  private readonly toaster = inject(ToasterService);

  readonly loadingInput = input(false, { alias: 'loading' });
  readonly history = input<StandTransformationHistoryViewRow | null>(null);
  readonly mode = input<StandTransformationHistoryDetailMode>('view');
  readonly distributionCommentMaxLength = DEFAULT_TEXTAREA_MAX_LENGTH;
  readonly distributionCommentMaxLengthMessageKey =
    'LanguageService::DefaultErrorMax';
  readonly distributionValueOptions = [
    {
      key: 'Si',
      value:
        'Geographic::Entity.StandTransformationHistory.InventoryDistributionOptionYes',
    },
    {
      key: 'No',
      value:
        'Geographic::Entity.StandTransformationHistory.InventoryDistributionOptionNo',
    },
  ];
  readonly form = this.formBuilder.group({
    inventoryDistributionValue: ['', [Validators.required]],
    inventoryDistributionComment: [
      '',
      [Validators.required, Validators.maxLength(this.distributionCommentMaxLength)],
    ],
    activityDistributionValue: ['', [Validators.required]],
    activityDistributionComment: [
      '',
      [Validators.required, Validators.maxLength(this.distributionCommentMaxLength)],
    ],
  });
  readonly saving = signal(false);
  readonly isInventoryEditMode = computed(
    () => this.mode() === 'editInventoryDistribution',
  );
  readonly isActivityEditMode = computed(
    () => this.mode() === 'editActivityDistribution',
  );
  readonly isEditMode = computed(
    () => this.isInventoryEditMode() || this.isActivityEditMode(),
  );
  readonly loading = computed(() => this.loadingInput() || this.saving());
  private readonly formCanSave = createFormCanSaveSignal(this.form);
  readonly canSave = computed(
    () =>
      !this.loading() &&
      (!!this.history() || this.isEditMode()) &&
      (!this.isEditMode() || this.formCanSave()),
  );
  private lastInitializedHistoryId: number | null = null;

  constructor() {
    effect(() => {
      const isInventoryEditMode = this.isInventoryEditMode();
      const isActivityEditMode = this.isActivityEditMode();

      this.setControlState(
        'inventoryDistributionValue',
        isInventoryEditMode,
      );
      this.setControlState(
        'inventoryDistributionComment',
        isInventoryEditMode,
      );
      this.setControlState(
        'activityDistributionValue',
        isActivityEditMode,
      );
      this.setControlState(
        'activityDistributionComment',
        isActivityEditMode,
      );
    });

    effect(() => {
      const history = this.history();
      if (!history?.id || history.id === this.lastInitializedHistoryId) {
        return;
      }

      this.lastInitializedHistoryId = history.id;
      this.form.reset(
        {
          inventoryDistributionValue: this.getInventoryDistributionValue(history),
          inventoryDistributionComment: history.inventoryDistributionComment ?? '',
          activityDistributionValue: this.getActivityDistributionValue(history),
          activityDistributionComment: history.activityDistributionComment ?? '',
        },
        { emitEvent: false },
      );
      this.form.markAsPristine();
    });
  }

  confirm(): void {
    if (!this.isEditMode()) {
      this.activeModal.close(false);
      return;
    }

    if (this.loading() || !this.history()) {
      return;
    }

    if (!this.validateBeforeSave()) {
      return;
    }

    const historyId = this.history()?.id;
    if (!historyId) {
      return;
    }

    this.saving.set(true);
    this.saveDistribution(historyId)
      .pipe(
        take(1),
        catchError(error => {
          const message =
            error?.error?.error?.message ??
            error?.error?.message ??
            this.getValidationMessageKey();

          this.toaster.error(message, '', { life: 5000 });
          return of(null);
        }),
        finalize(() => {
          this.saving.set(false);
        }),
      )
      .subscribe(result => {
        if (!result) {
          return;
        }

        this.form.markAsPristine();
        this.activeModal.close({ saved: true });
      });
  }

  dismiss(): void {
    if (this.saving()) {
      return;
    }

    if (!this.isEditMode() || !this.form.dirty) {
      this.activeModal.dismiss(false);
      return;
    }

    this.confirmationService
      .showCancelConfirmation({
        messageKey: this.getCloseConfirmMessageKey(),
        titleKey: this.getCloseConfirmTitleKey(),
      })
      .pipe(take(1))
      .subscribe(status => {
        if (status === Confirmation.Status.confirm) {
          this.resetFormToHistory();
          this.activeModal.dismiss(false);
        }
      });
  }

  getNestedText(
    row: Record<string, any> | null | undefined,
    paths: string[],
    fallback = '-',
  ): string {
    if (!row) {
      return fallback;
    }

    for (const path of paths) {
      const value = path.split('.').reduce<any>((current, segment) => current?.[segment], row);
      if (value !== null && value !== undefined && value !== '') {
        return String(value);
      }
    }

    return fallback;
  }

  formatDate(value: string | null | undefined, fallback = '-'): string {
    if (!value) {
      return fallback;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return fallback;
    }

    const compactMatch = /^(\d{4})(\d{2})(\d{2})$/.exec(trimmedValue);
    if (compactMatch) {
      const [, year, month, day] = compactMatch;
      return `${day}/${month}/${year}`;
    }

    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(trimmedValue);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${day}/${month}/${year}`;
    }

    return trimmedValue;
  }

  private validateBeforeSave(): boolean {
    const commentControl = this.isActivityEditMode()
      ? this.form.controls.activityDistributionComment
      : this.form.controls.inventoryDistributionComment;

    if (commentControl.hasError('maxlength')) {
      this.form.markAllAsTouched();
      this.toaster.error(this.distributionCommentMaxLengthMessageKey, '', {
        life: 5000,
        messageLocalizationParams: [String(this.distributionCommentMaxLength)],
      });
      return false;
    }

    const {
      value,
      comment,
    } = this.isActivityEditMode()
      ? {
          value:
            this.form.controls.activityDistributionValue.value?.trim() ?? '',
          comment:
            this.form.controls.activityDistributionComment.value?.trim() ?? '',
        }
      : {
          value:
            this.form.controls.inventoryDistributionValue.value?.trim() ?? '',
          comment:
            this.form.controls.inventoryDistributionComment.value?.trim() ?? '',
        };

    if (value && comment) {
      return true;
    }

    this.form.markAllAsTouched();
    this.toaster.error(
      this.getValidationMessageKey(),
      '',
      { life: 5000 },
    );
    return false;
  }

  private getInventoryDistributionValue(
    history: StandTransformationHistoryViewRow,
  ): string {
    const statusName =
      history.inventoryDistributionStatus?.name ??
      history.inventoryDistributionStatusName ??
      '';

    if (statusName === 'Si' || statusName === 'No') {
      return statusName;
    }

    return '';
  }

  private getActivityDistributionValue(
    history: StandTransformationHistoryViewRow,
  ): string {
    const statusName =
      history.activityDistributionStatus?.name ??
      history.activityDistributionStatusName ??
      '';

    if (statusName === 'Si' || statusName === 'No') {
      return statusName;
    }

    return '';
  }

  private resetFormToHistory(): void {
    const history = this.history();

    this.form.reset(
      {
        inventoryDistributionValue: history
          ? this.getInventoryDistributionValue(history)
          : '',
        inventoryDistributionComment: history?.inventoryDistributionComment ?? '',
        activityDistributionValue: history
          ? this.getActivityDistributionValue(history)
          : '',
        activityDistributionComment: history?.activityDistributionComment ?? '',
      },
      { emitEvent: false },
    );
    this.form.markAsPristine();
  }

  private saveDistribution(historyId: number) {
    if (this.isActivityEditMode()) {
      const payload: EditActivityDistributionInputDto = {
        standTransformationHistoryId: historyId,
        activityDistributionValue:
          this.form.controls.activityDistributionValue.value?.trim() ?? '',
        activityDistributionComment:
          this.form.controls.activityDistributionComment.value?.trim() ?? '',
      };

      return this.orchestratorService.editActivityDistribution(payload);
    }

    const payload: EditInventoryDistributionInputDto = {
      standTransformationHistoryId: historyId,
      inventoryDistributionValue:
        this.form.controls.inventoryDistributionValue.value?.trim() ?? '',
      inventoryDistributionComment:
        this.form.controls.inventoryDistributionComment.value?.trim() ?? '',
    };

    return this.orchestratorService.editInventoryDistribution(payload);
  }

  private getValidationMessageKey(): string {
    return this.isActivityEditMode()
      ? 'Geographic::Entity.StandTransformationHistory.ActivityDistributionValidationMessage'
      : 'Geographic::Entity.StandTransformationHistory.InventoryDistributionValidationMessage';
  }

  private getCloseConfirmMessageKey(): string {
    return this.isActivityEditMode()
      ? 'Geographic::Entity.StandTransformationHistory.ActivityDistributionCloseConfirmMessage'
      : 'Geographic::Entity.StandTransformationHistory.InventoryDistributionCloseConfirmMessage';
  }

  private getCloseConfirmTitleKey(): string {
    return this.isActivityEditMode()
      ? 'Geographic::Entity.StandTransformationHistory.ActivityDistributionCloseConfirmTitle'
      : 'Geographic::Entity.StandTransformationHistory.InventoryDistributionCloseConfirmTitle';
  }

  private setControlState(
    controlName:
      | 'inventoryDistributionValue'
      | 'inventoryDistributionComment'
      | 'activityDistributionValue'
      | 'activityDistributionComment',
    enabled: boolean,
  ): void {
    const control = this.form.controls[controlName];
    if (enabled) {
      control.enable({ emitEvent: false });
      return;
    }

    control.disable({ emitEvent: false });
  }
}
